import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Icon } from "semantic-ui-react";

import { useSelectedDealer, useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { setSearchTerm, useSearchTerm } from "components/NavBarSearch/hooks";
import { SearchDropdown } from "components/NavBarSearchAppointments/components";
import { useAppointmentSearchResults } from "components/NavBarSearchAppointments/hooks/useAppointmentSearchResults";

const MINIMUM_SEARCH_LENGTH = 3;

export const NavBarSearchAppointments = () => {
  const history = useHistory();
  const keepSearchRef = useRef(false);
  const [lastEnteredSearchTerm, setLastEnteredSearchTerm] = useState("");

  const searchTerm = useSearchTerm();
  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();
  const { searchResults, isFetchingAppointments, refetch, reset } = useAppointmentSearchResults(searchTerm, selectedLocation, selectedDealer);

  const canSearch = Number(searchTerm.length) > MINIMUM_SEARCH_LENGTH;

  const clearSearch = () => {
    reset();
    setSearchTerm("");
  };

  useEffect(() => {
    if (canSearch) refetch();

    return () => {
      if (!keepSearchRef.current) clearSearch();
    };
  }, []);

  useEffect(() => {
    const inputElement = document.getElementById("NavBarSearchInput");
    if (!inputElement) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || !canSearch) return;
      if (lastEnteredSearchTerm !== searchTerm) setLastEnteredSearchTerm(searchTerm);
      refetch();
    };

    inputElement.addEventListener("keydown", handleKeyDown);

    return () => inputElement.removeEventListener("keydown", handleKeyDown);
  }, [canSearch, refetch, searchTerm, lastEnteredSearchTerm]);

  useEffect(() => {
    setLastEnteredSearchTerm(prev => {
      if (prev === searchTerm) return prev;
      if (!searchTerm) reset();
      return searchTerm;
    });
  }, [searchTerm]);

  const handleSelect = (id: string) => {
    keepSearchRef.current = true;
    history.push(`/appointments/${id}`);
  };

  if (!selectedLocation || (!searchResults && !canSearch)) return null;

  return (
    <>
      <Icon
        className="ellipsis grey list-view search-icon"
        onClick={() => {
          refetch();
          setLastEnteredSearchTerm(searchTerm);
        }}
      />
      <Icon className="xmark grey list-view clear-icon" onClick={clearSearch} />
      {searchResults && lastEnteredSearchTerm && (
        <SearchDropdown data={searchResults} isFetchingAppointments={isFetchingAppointments} onSelect={handleSelect} onClose={reset} />
      )}
    </>
  );
};
